import { NetworkStatus, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DebouncedSearchInput,
  ListHeaderCloseButton,
  ListHeaderSearchButton,
  useScreenWidth,
} from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { difference } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useLatest, useUpdateEffect } from "react-use";
import {
  namedOperations,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { DocDataGrid } from "../../features/doc-data-grid/DocDataGrid.js";
import { createDocDataGridInputCell } from "../../features/doc-data-grid/DocDataGridInputCell.js";
import { createDocDataGridItemCell } from "../../features/doc-data-grid/DocDataGridItemCell.js";
import { DocDataGridSelectionContext } from "../../features/doc-data-grid/DocDataGridSelectionContext.js";
import { useBatchMoveTreeItems } from "../../features/doc-data-grid/useBatchMoveTreeItems.js";
import {
  filterRedundantChildren,
  sortItemIdsByTreeOrder,
} from "../../features/doc-items/tree-helpers.js";
import { QuoteAnnotationsBanner } from "../../features/quote-annotations/QuoteAnnotationsBanner.js";
import { QuoteItemContextMenuItems } from "../../features/quotes/QuoteItemContextMenuItems.js";
import { useQuoteMoveTargets } from "../../features/quotes/useQuoteMoveTargets.js";
import { useQuoteColumns } from "../../features/doc-data-grid/quote/useQuoteColumns.js";
import { useCardExpandedStore } from "../../features/users/useCardExpandedStore.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import type {
  DocDataGridRow,
  PendingCellEdit,
} from "../../features/doc-data-grid/types.js";
import { useDocDataGridRows } from "../../features/doc-data-grid/useDocDataGridRows.js";
import { ProfitabilityBox } from "../../features/doc-items/boxes/ProfitabilityBox.js";
import { ButtonItemBindingSelectMultiple } from "../../features/doc-items/fields/ButtonItemBindingSelectMultiple.js";
import { ButtonItemTypeSelectMultiple } from "../../features/doc-items/fields/ButtonItemTypeSelectMultiple.js";
import {
  getViewerDecisionRole,
  isTreePreviewItemVisible,
} from "../../features/doc-items/helpers.js";
import type { ItemChange } from "../../features/doc-items/hooks/useItemBindings.js";
import { useItemsSearch } from "../../features/doc-items/hooks/useItemsSearch.js";
import { ItemSearchChips } from "../../features/doc-items/ItemSearchChips.js";
import { DecisionsInBulkProcessRef } from "../../features/doc-items/modals/DecisionsInBulkProcess.js";
import { createTreeItemInput } from "../../features/projects/quote-trees.js";
import {
  allowHaveChildren,
  itemCanBeEdited,
  shouldRenderCreateInput,
} from "../../features/quotes/helpers.js";
import { QuoteAction } from "../../features/quotes/QuoteAction.js";
import { QuoteDecisionsInBulkProcess } from "../../features/quotes/QuoteDecisionsInBulkProcess.js";
import { QuotePriceSummaryBox } from "../../features/quotes/QuotePriceSummaryBox.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { QuoteTimeSummaryBox } from "../../features/quotes/QuoteTimeSummaryBox.js";
import { useCreateQuoteItems } from "../../features/quotes/useCreateQuoteItems.js";
import { VanMarckeProfitability } from "../../features/van-marcke-profitability/VanMarckeProfitability.js";
import { ROOT_ITEM_ID } from "../../trees-virtual/helpers.js";
import { useEnrichExpandedStoreWithItemIds } from "../../trees-virtual/hooks/useEnrichExpandedStoreWithItemIds.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../trees-virtual/hooks/useExpandedStore.js";
import {
  AdditionalItemInput,
  TreeViewMode,
} from "../../trees-virtual/types.js";
import { TreeToggleAllExpandedButton } from "../../trees/components/TreeToggleButton.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
} from "../../utils.js";
import { useProjectQuoteContext } from "./ProjectQuoteContext.js";
import {
  ProjectQuoteDataGrid_ItemFragment,
  ProjectQuoteDataGrid_ItemFragmentDoc,
  useProjectQuoteDataGridQuery,
} from "./ProjectQuoteDataGrid.generated.js";

const REFETCH_QUERIES = [namedOperations.Query.ProjectQuoteDataGrid];

interface Props {
  projectId: string;
  quoteId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export const ProjectQuoteDataGrid = ({
  projectId,
  quoteId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const pathToDocList = `/projects/${projectId}/quotes`;
  const pathToDoc = `/projects/${projectId}/quotes/${quoteId}`;
  const pathToDocPage = `${pathToDoc}/grid`;
  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const latestLocation = useLatest(location);

  // ---------------------------------------------------------------------------
  // Multi-select state
  // ---------------------------------------------------------------------------

  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(),
    });

  const checkedItemIds = React.useMemo(
    () => [...rowSelectionModel.ids].map(String),
    [rowSelectionModel.ids]
  );

  const isItemChecked = React.useCallback(
    (id: string) => rowSelectionModel.ids.has(id),
    [rowSelectionModel.ids]
  );

  const clearChecked = React.useCallback(
    () => setRowSelectionModel({ type: "include", ids: new Set() }),
    []
  );

  // ---------------------------------------------------------------------------
  // additionalItemInput state (task 7.1)
  // ---------------------------------------------------------------------------

  const [additionalItemInput, setAdditionalItemInput] =
    React.useState<AdditionalItemInput | null>(null);
  const additionalItemInputRef = useLatest(additionalItemInput);
  // set when an item is created from a positioned input — the clearing effect
  // re-anchors a new positioned input below it instead of clearing
  const pendingReanchorItemIdRef = React.useRef<string | null>(null);

  const onAdditionalItemInputChange = React.useCallback(
    (input: AdditionalItemInput) => {
      setAdditionalItemInput(input);
      setTimeout(() => {
        const inputRank =
          input.itemRank + (input.position === "below" ? 0.5 : -0.5);
        if (!isInTestMode())
          createRecursiveTryFocusElementFunction(
            `input#msys-datagrid-input-${input.itemParentType}-${input.itemParentId}[data-node-rank="${inputRank}"]:enabled`
          )();
      });
    },
    []
  );

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["QuoteEdit", "Global", "Quote"]);

  const isClientVisibilityEnabled = useFeature("ClientVisibility");

  const { isMinTablet } = useScreenWidth();

  // ---------------------------------------------------------------------------
  // Expanded store (task 7.2)
  // ---------------------------------------------------------------------------

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote",
    quoteId,
    selectedItemId
  );

  const client = useApolloClient();
  const query = useProjectQuoteDataGridQuery({
    client,
    variables: {
      projectId,
      quoteId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.quote)?.quote;
  const docTitle = doc?.title ?? "";

  // ---------------------------------------------------------------------------
  // Navigation callbacks (task 7.3)
  // ---------------------------------------------------------------------------

  const navigateToItem = React.useCallback(
    (id: string) =>
      navigate(
        {
          pathname: `${pathToDocPage}/items/${id}`,
          search: latestLocation.current.search,
        },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );

  const navigateToDocPage = React.useCallback(
    () =>
      navigate(
        { pathname: pathToDocPage, search: latestLocation.current.search },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );

  // ---------------------------------------------------------------------------
  // Items search (task 7.4)
  // ---------------------------------------------------------------------------

  const {
    filterItemSearch,
    filters,
    setFilters,
    resetFilters,
    search,
    setSearch,
    searchRef,
    toggleSearchVisible,
    searchVisible,
    isSearchOrFiltersEnabled,
    parentItemIds,
    isItemMuted,
  } = useItemsSearch({
    projectId,
    docId: quoteId,
    onSearchApplied: ({ parentItemIds }) => {
      if (
        difference(parentItemIds, expandedStore.expandedItemIds ?? []).length >
        0
      ) {
        expandedStore.setItemsExpanded(parentItemIds, true);
      }
    },
  });

  const showExpandButton = React.useCallback(
    (item: ProjectQuoteDataGrid_ItemFragment) => {
      if (!allowHaveChildren(item)) return false;
      if (!isSearchOrFiltersEnabled) return item.hasChildren;
      return item.hasChildren && parentItemIds.includes(item.id);
    },
    [isSearchOrFiltersEnabled, parentItemIds]
  );

  const quote = getDataOrNull(query.data?.quote)?.quote;
  const viewerPermissions: PermissionName[] = React.useMemo(
    () => quote?.viewerPermissions ?? [],
    [quote?.viewerPermissions]
  );

  const contractType = doc?.contractType;
  const viewerDecisionRole = getViewerDecisionRole(doc?.actors ?? []);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback(
    (props: ConfirmModalProps) =>
      confirmProcessRef.current!.startConfirmProcess(props),
    []
  );

  const decisionsInBulkProcessRef =
    React.useRef<DecisionsInBulkProcessRef>(null);
  const startDecisionsInBulkProcess = React.useCallback(() => {
    decisionsInBulkProcessRef.current?.startDecisionsInBulkProcess();
  }, []);

  const { selectWizardContextButton, refetchWizardQuestions } =
    useProjectQuoteContext();

  // ---------------------------------------------------------------------------
  // Create items (task 7.5)
  // ---------------------------------------------------------------------------

  const onItemCreated = React.useCallback(
    async (newItem?: { id: string }) => {
      // if the item was created from a positioned input, remember it so the
      // clearing effect can re-open a positioned input below it
      if (additionalItemInputRef.current && newItem) {
        pendingReanchorItemIdRef.current = newItem.id;
      }
      await refetchWizardQuestions();
    },
    [additionalItemInputRef, refetchWizardQuestions]
  );

  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
  } = useCreateQuoteItems({
    projectId,
    quoteId,
    expandedItemIds: expandedStore.expandedItemIds,
    onItemCreated,
    source: "grid",
  });

  // ---------------------------------------------------------------------------
  // Batch move
  // ---------------------------------------------------------------------------

  const [batchMove] = useBatchMoveTreeItems({
    projectId,
    docId: quoteId,
  });

  const getValidMoveTargets = useQuoteMoveTargets({
    projectId,
    docId: quoteId,
  });

  const handleMoveRef = React.useRef<
    (targetItemId: string, itemIds: string[]) => Promise<void>
  >(async () => {});

  const ItemComponent = React.useMemo(() => {
    if (contractType && quote?.rootItem.id) {
      return createDocDataGridItemCell<ProjectQuoteDataGrid_ItemFragment>({
        pathToDocPage,
        projectId,
        doc: {
          id: quoteId,
          type: "QUOTE",
          rootItemId: quote.rootItem.id,
          contractType,
          viewerPermissions,
          owningSystemOrganisationId: quote.owningSystemOrganisationId,
        },
        startConfirmProcess,
        navigateToItem,
        expandedStore,
        pasteItem,
        isAllowedToPasteItem,
        onAdditionalItemInputChange,
        getValidMoveTargets,
        onMoveItems: (...args) => handleMoveRef.current(...args),
        additionalMenuItems: item => (
          <QuoteItemContextMenuItems
            projectId={projectId}
            quoteId={quoteId}
            item={item}
            quote={{
              id: quoteId,
              owningSystemOrganisationId: quote.owningSystemOrganisationId,
              viewerPermissions,
            }}
            navigateToItem={navigateToItem}
          />
        ),
      });
    }
    return () => null;
  }, [
    contractType,
    quote?.rootItem.id,
    quote?.owningSystemOrganisationId,
    pathToDocPage,
    projectId,
    quoteId,
    viewerPermissions,
    startConfirmProcess,
    navigateToItem,
    expandedStore,
    pasteItem,
    isAllowedToPasteItem,
    onAdditionalItemInputChange,
  ]);

  const InputComponent = React.useMemo(
    () =>
      createDocDataGridInputCell<ProjectQuoteDataGrid_ItemFragment>({
        viewMode: TreeViewMode.DataGrid,
        doc: { id: quoteId, docType: "QUOTE", templateIsDraft: false },
        documentItemTypes: QUOTE_ITEM_TYPES,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      quoteId,
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
    ]
  );

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        viewMode: TreeViewMode.Item,
        quoteId,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
      quoteId,
    ]
  );

  // ---------------------------------------------------------------------------
  // Items filtering (task 7.6)
  // ---------------------------------------------------------------------------

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems?.find(item => item.isRootItem);
    const treeItems = allDocItems
      .filter(item => item.authorOrganisationId === viewer.organisation.id)
      .filter(item => !item.isAnyParentItemEliminated);
    const filteredItems = treeItems.filter(filterItemSearch).map(item => {
      if (!item.isRootItem) return item;
      return { ...item, parentId: ROOT_ITEM_ID };
    });
    return [filteredItems, rootItem, allDocItems];
  }, [doc, filterItemSearch, viewer.organisation.id]);

  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  const enableCreating = isViewersQuote ? true : false;
  const enableDragging = isViewersQuote ? !isAgreed : false;
  const enableNavigating = isViewersQuote ? true : false;

  // ---------------------------------------------------------------------------
  // Inline cell editing (tasks 7.1–7.6)
  // ---------------------------------------------------------------------------

  const canEdit = isViewersQuote && !isAgreed;

  const { enqueueSnackbar } = useSnackbar();

  const [savingRowIds, setSavingRowIds] = React.useState<Set<string>>(
    () => new Set()
  );

  const isSavingRow = React.useCallback(
    (rowId: string) => savingRowIds.has(rowId),
    [savingRowIds]
  );

  const handleMove = React.useCallback(
    async (targetItemId: string, itemIds: string[]) => {
      const filteredIds = filterRedundantChildren(itemIds, allDocItems);
      const sortedIds = sortItemIdsByTreeOrder(filteredIds, allDocItems);
      const allSavingIds = [...itemIds, targetItemId];
      setSavingRowIds(prev => {
        const next = new Set(prev);
        for (const id of allSavingIds) next.add(id);
        return next;
      });
      try {
        await batchMove(sortedIds, targetItemId);
        clearChecked();
      } finally {
        setSavingRowIds(prev => {
          const next = new Set(prev);
          for (const id of allSavingIds) next.delete(id);
          return next;
        });
      }
    },
    [allDocItems, batchMove, clearChecked]
  );
  handleMoveRef.current = handleMove;

  const stateStore = useDataGridStateStore("ProjectQuoteDataGrid");

  const timeStore = useCardExpandedStore("quote-time-summary", false);
  const priceStore = useCardExpandedStore("quote-price-summary", false);
  const profitabilityStore = useCardExpandedStore("quote-profitability", false);

  const { columns, initialState } = useQuoteColumns(
    doc && quote
      ? {
          projectId,
          docId: quoteId,
          contractType: doc.contractType,
          canEdit,
          itemCanBeEdited,
          expandedItemIds: expandedStore.expandedItemIds,
          setItemExpanded: expandedStore.setItemExpanded,
          viewerIsContractor: isViewersQuote,
          quote,
        }
      : {
          projectId,
          docId: quoteId,
          contractType: undefined,
          canEdit: false,
          expandedItemIds: undefined,
          viewerIsContractor: false,
          quote: {
            id: quoteId,
            owningSystemOrganisationId: "",
            viewerPermissions: [],
          },
        }
  );

  const processRowUpdate = React.useCallback(
    async (
      newRow: DocDataGridRow<ProjectQuoteDataGrid_ItemFragment> & {
        _pendingEdit?: PendingCellEdit<
          Partial<ProjectQuoteDataGrid_ItemFragment>
        >;
      },
      oldRow: DocDataGridRow<ProjectQuoteDataGrid_ItemFragment>
    ) => {
      if (!newRow._pendingEdit || !newRow.item) return newRow;
      setSavingRowIds(prev => {
        const next = new Set(prev);
        next.add(newRow.id);
        return next;
      });
      try {
        await newRow._pendingEdit.apply(newRow.item.id);
        const updatedItem = client.readFragment({
          id: client.cache.identify({
            __typename: "Item",
            id: newRow.item.id,
            originVersionNumber: newRow.item.originVersionNumber,
          }),
          fragment: ProjectQuoteDataGrid_ItemFragmentDoc,
          fragmentName: "ProjectQuoteDataGrid_Item",
        });
        return { ...newRow, item: { ...newRow.item, ...updatedItem } };
      } catch (e) {
        if (e instanceof Error)
          enqueueSnackbar(e.message, { variant: "error" });
        throw e;
      } finally {
        setSavingRowIds(prev => {
          const next = new Set(prev);
          next.delete(newRow.id);
          return next;
        });
      }
    },
    [client, enqueueSnackbar]
  );

  // ---------------------------------------------------------------------------
  // Helper effects (task 7.7)
  // ---------------------------------------------------------------------------

  // Reset additionalItemInput when selection or data changes — or re-anchor a
  // positioned input below an item that was just created from a positioned input
  useUpdateEffect(() => {
    const reanchorItemId = pendingReanchorItemIdRef.current;
    if (reanchorItemId) {
      const newItem = allDocItems?.find(i => i.id === reanchorItemId);
      const newItemParent =
        newItem?.parentId != null
          ? allDocItems?.find(i => i.id === newItem.parentId)
          : null;
      if (newItem && newItem.parentId && newItemParent) {
        pendingReanchorItemIdRef.current = null;
        onAdditionalItemInputChange({
          itemId: newItem.id,
          itemParentId: newItem.parentId,
          itemParentType: newItemParent.type,
          itemRank: newItem.rank,
          position: "below",
        });
      }
      // keep the pending id until the re-anchor succeeds so a later
      // allDocItems update (once the new item lands) can retry
      return;
    }
    setAdditionalItemInput(null);
  }, [selectedItemId, allDocItems, onAdditionalItemInputChange]);

  // Redirect to doc page when selected item is filtered out
  React.useEffect(() => {
    if (
      !query.loading &&
      selectedItemId &&
      !filteredTreeItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigateToDocPage();
    }
  }, [
    selectedItemId,
    filteredTreeItems,
    pathToDocPage,
    rootItem,
    query.loading,
    navigateToDocPage,
  ]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  useEnrichExpandedStoreWithItemIds(
    expandedStore,
    allDocItems,
    !query.loading &&
      !query.error &&
      query.networkStatus === NetworkStatus.ready,
    showExpandButton
  );

  const filterItem = React.useCallback(
    (item: ProjectQuoteDataGrid_ItemFragment) =>
      isTreePreviewItemVisible(true, item, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  // ---------------------------------------------------------------------------
  // Build rows
  // ---------------------------------------------------------------------------

  const selectedItem = selectedItemId
    ? (filteredTreeItems.find(i => i.id === selectedItemId) ?? null)
    : null;

  const rows = useDocDataGridRows(
    filteredTreeItems,
    rootItem,
    selectedItem,
    additionalItemInput,
    enableCreating,
    QUOTE_ITEM_TYPES,
    isClientVisibilityEnabled ? filterItem : undefined,
    shouldRenderCreateInput
  );

  // ---------------------------------------------------------------------------
  // Page JSX (task 7.8)
  // ---------------------------------------------------------------------------

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc ? (
          <QuoteSubHeader
            project={project}
            quote={doc}
            activeView="grid"
            canEdit={isViewersQuote}
            isReadOnly={isAgreed}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={false}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={expandedStore.expandedItemIds}
          />
        ) : undefined
      }
      action={
        project &&
        doc && (
          <QuoteAction
            doc={doc}
            project={project}
            onDiscardPendingChangesRefetchQueries={REFETCH_QUERIES}
            onDeclineChangesRefetchQueries={REFETCH_QUERIES}
            startDecisionsInBulkProcess={startDecisionsInBulkProcess}
            expandedItemIds={expandedStore.expandedItemIds}
            viewerDecisionRole={viewerDecisionRole}
          />
        )
      }
    >
      {doc && project && (
        <PageContainer $display="flex" $noPadding $fullHeight>
          <Stack
            direction="column"
            spacing={2}
            sx={{
              py: 2,
              flex: 1,
              // minHeight/minWidth 0: lets children with `flex: 1` and
              // `min-height: <px>` actually shrink below their content size.
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Box sx={{ px: 2 }}>
              <QuoteAnnotationsBanner />
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                width: "100%",
                alignItems: "center",
                px: 2,
              }}
            >
              <TreeToggleAllExpandedButton
                areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                setAllItemsExpanded={expandedStore.setAllItemsExpanded}
              />
              <ButtonItemTypeSelectMultiple
                value={filters.type ?? []}
                onChange={value => {
                  setFilters(f => ({
                    ...f,
                    type: value.length > 0 ? value : undefined,
                  }));
                }}
              />
              <ButtonItemBindingSelectMultiple
                value={[
                  ...(filters.binding ?? []),
                  ...(filters.needsAgreement ? ["change" as ItemChange] : []),
                ]}
                onChange={value => {
                  const bindings = value.filter(v => v !== "change");
                  const needsAgreement = value.some(v => v === "change");
                  setFilters(f => ({
                    ...f,
                    binding: bindings.length > 0 ? bindings : undefined,
                    needsAgreement: needsAgreement || undefined,
                  }));
                }}
                hasAgreement={doc?.agreement === "YES"}
              />
              {selectWizardContextButton}
              {!isMinTablet && !searchVisible && (
                <ListHeaderSearchButton onClick={toggleSearchVisible} />
              )}
              {isMinTablet && (
                <DebouncedSearchInput
                  inputRef={searchRef}
                  placeholder={t("Search", { ns: "Global" })}
                  defaultValue={search}
                  onChangeSearchTerm={setSearch}
                />
              )}
            </Stack>

            <ItemSearchChips
              filters={filters}
              setFilters={setFilters}
              onReset={resetFilters}
              sx={{ px: 2 }}
            />

            {!isMinTablet && searchVisible && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  width: "100%",
                  alignItems: "center",
                  px: 2,
                }}
              >
                <DebouncedSearchInput
                  inputRef={searchRef}
                  placeholder={t("Search", { ns: "Global" })}
                  defaultValue={search}
                  onChangeSearchTerm={setSearch}
                />
                <ListHeaderCloseButton onClick={toggleSearchVisible} />
              </Stack>
            )}

            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 300,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                px: 2,
              }}
            >
              <DocDataGridSelectionContext.Provider
                value={{ checkedItemIds, isItemChecked, clearChecked }}
              >
                <DocDataGrid<ProjectQuoteDataGrid_ItemFragment>
                  rows={rows}
                  projectId={enableDragging ? projectId : null}
                  docId={enableDragging ? quoteId : null}
                  expandedRowIds={expandedStore.expandedItemIds}
                  onExpandedRowIdChange={expandedStore.setItemExpanded}
                  isItemExpanded={expandedStore.isItemExpanded}
                  showExpandButton={showExpandButton}
                  selectedItemId={selectedItemId}
                  enableDragging={enableDragging}
                  documentItemTypes={QUOTE_ITEM_TYPES}
                  ItemComponent={ItemComponent}
                  InputComponent={InputComponent}
                  docAgreement={doc.agreement}
                  isItemMuted={isItemMuted}
                  columns={columns}
                  processRowUpdate={processRowUpdate}
                  stateStore={stateStore}
                  initialState={initialState}
                  isSavingRow={isSavingRow}
                  enableMultiSelect={isViewersQuote && !isAgreed}
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={setRowSelectionModel}
                />
              </DocDataGridSelectionContext.Provider>
            </Box>

            <QuoteDecisionsInBulkProcess
              ref={decisionsInBulkProcessRef}
              projectId={projectId}
              quoteId={doc.id}
              canFinalize={true}
              externalExpandedItemIds={expandedStore.expandedItemIds}
            />

            <Stack
              direction="column"
              spacing={2}
              sx={{ px: 2, alignItems: "stretch" }}
            >
              {doc.proposedCalculation && (
                <QuoteTimeSummaryBox
                  key={`quote_time_summary-${isAgreed}`}
                  calculated={doc.proposedCalculation}
                  isExpanded={timeStore.isExpanded}
                  onExpandedChange={timeStore.setIsExpanded}
                />
              )}

              {doc.proposedCalculation && (
                <QuotePriceSummaryBox
                  key={`quote_price_summary-${isAgreed}`}
                  isManualSave={isAgreed}
                  quoteId={quoteId}
                  quote={doc}
                  project={project}
                  discountPercentage={doc.discountPercentage}
                  calculated={doc.proposedCalculation}
                  isExpanded={priceStore.isExpanded}
                  onExpandedChange={priceStore.setIsExpanded}
                />
              )}

              {doc.proposedCalculation && (
                <ProfitabilityBox
                  calculated={doc.proposedCalculation}
                  isExpanded={profitabilityStore.isExpanded}
                  onExpandedChange={profitabilityStore.setIsExpanded}
                />
              )}

              <VanMarckeProfitability projectId={projectId} docId={quoteId} />
            </Stack>
          </Stack>
        </PageContainer>
      )}
      <ConfirmProcess ref={confirmProcessRef} />
      <Outlet
        context={{
          expandedItemIds: expandedStore.expandedItemIds,
          CreateChildComponent: TreeItemInput,
          pasteItem,
          isAllowedToPasteItem,
          viewerDecisionRole,
        }}
      />
    </Page>
  );
};
